import type { CacheUpdateApiParams } from './cache_update_api_params';
import type { Fund } from './fund';
import type { FundConflatedAsset } from './fund_conflated_asset';
import { getBestSourceId, matchesSourceId } from './fund_conflated_asset';
import { PAGINATION } from './pagination';

export const CURRENT_SETTINGS_VERSION = 1;

export interface PortfolioFund {
  isin: string;
  units: number;
  price?: number;
  name?: string;
}

export interface PortfolioAsset {
  id: string;
  units: number;
  price?: number;
  name?: string;
}

export interface Portfolio {
  name: string;
  funds: PortfolioFund[];
  assets: PortfolioAsset[];
}

export interface Settings {
  version: number;
  accessToken?: string;
  cacheUpdateApiParams?: CacheUpdateApiParams;
  defaultPageSize: number;
  conflateHoldings: boolean;
  dontShowPortfolioHints?: boolean;
  dontShowDataNotice?: boolean;
  dontShowAlertsNotice?: boolean;
  theme?: 'light' | 'dark';
  country?: string;
  portfolios: Portfolio[];
}

export function settingsGetBaseCurrency() {
  return '€';
}

function isFund(item: Fund | FundConflatedAsset): item is Fund {
  return 'isin' in item;
}

export function settingsSavePortfolio(settings: Settings, name: string, item: Fund | FundConflatedAsset): Settings {
  const trimmedName = name.trim();
  if (!name) {
    return settings;
  }

  const existingPortfolio = settings.portfolios.find((group) => group.name === trimmedName);
  if (existingPortfolio) {
    if (isFund(item)) {
      if (!existingPortfolio.funds.some((existingFund) => existingFund.isin === item.isin)) {
        existingPortfolio.funds.push({ isin: item.isin, units: 100 });
      }
    } else {
      if (!existingPortfolio.assets.some((existingAsset) => matchesSourceId(item, existingAsset.id))) {
        existingPortfolio.assets.push({ id: getBestSourceId(item), units: 100 });
      }
    }
  } else {
    settings.portfolios.push(
      isFund(item)
        ? { name: trimmedName, funds: [{ isin: item.isin, units: 100 }], assets: [] }
        : { name: trimmedName, funds: [], assets: [{ id: getBestSourceId(item), units: 100 }] },
    );
  }

  return { ...settings };
}

export function settingsAddPortfolio(settings: Settings, portfolio: Portfolio): Settings {
  const existingPortfolioIndex = settings.portfolios.findIndex((group) => group.name === portfolio.name);
  if (existingPortfolioIndex < 0) {
    return { ...settings, portfolios: [...settings.portfolios, portfolio] };
  }

  settings.portfolios.splice(existingPortfolioIndex, 1, portfolio);

  return { ...settings, portfolios: [...settings.portfolios] };
}

export function settingsRemovePortfolio(settings: Settings, portfolioName: string): Settings {
  const existingPortfolioIndex = settings.portfolios.findIndex((group) => group.name === portfolioName);
  if (existingPortfolioIndex < 0) {
    return settings;
  }

  settings.portfolios.splice(existingPortfolioIndex, 1);

  return { ...settings };
}

export function settingsSetPortfolios(settings: Settings, portfolios: Portfolio[]) {
  return { ...settings, portfolios: [...portfolios] };
}

export function settingsDontShowPortfolioHints(settings: Settings): Settings {
  return { ...settings, dontShowPortfolioHints: true };
}

export function settingsDontShowDataNotice(settings: Settings): Settings {
  return { ...settings, dontShowDataNotice: true };
}

export function settingsDontShowAlertsNotice(settings: Settings): Settings {
  return { ...settings, dontShowAlertsNotice: true };
}

export function settingsSetAccessToken(settings: Settings, accessToken: string): Settings {
  return { ...settings, accessToken };
}

export function settingsSetCacheUpdateApiParams(
  settings: Settings,
  cacheUpdateApiParams: CacheUpdateApiParams,
): Settings {
  return { ...settings, cacheUpdateApiParams };
}

export function settingsDefault(): Settings {
  return {
    version: CURRENT_SETTINGS_VERSION,
    defaultPageSize: PAGINATION.defaultPageSize,
    conflateHoldings: true,
    country: 'gb',
    portfolios: [],
  };
}
